import { Box, Typography } from "@material-ui/core";
import { Loader, styled, useRouter } from "@shoothill/core";
import { ITab, Tabs } from "Components/Tabs/Tabs";
import { useObserver } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { runInAction } from "mobx";
import { InductionAdminViewModel } from "./InductionAdminViewModel";
import { InductionAdminDetailsView } from "./Details/InductionAdminDetailsView";
import { InductionAdminSiteAccessLogView } from "./SiteAccessLog/InductionAdminSiteAccessLogView";
import { PreviewImage } from "../../../../Globals/Styles/AppStyling";
import { SHBox } from "../../../../Components/Box";
import { PrimaryButton } from "Components/Buttons/Buttons";
import { DeleteModal } from "Components/Modal/DeleteModal";
import { SnackBar } from "Components/SnackBar/SnackBar";
import { AdminSiteAccessFilterViewModel } from "./SiteAccessLog/AdminSiteAccessFilterViewModel";
import moment from "moment";
import { InductionAdminScaffoldingView } from "./Scaffold/InductionAdminScaffoldingView";
import { InductionAdminPermitView } from "./Permits/InductionAdminPermitView";
import { InductionAdminNearMissView } from "./NearMiss/InductionAdminNearMissView";
import { InductionAdminInspectionView } from "./Inspection/InductionAdminInspectionView";
import { InductionAdminNonConformanceView } from "./NonConformance/InductionAdminNonConformanceView";
import { StoresInstance } from "Globals/Stores";

interface IProps {
    className?: string;
}

export const TAB_DETAILS = "details";
export const TAB_SITEACCESSLOG = "siteaccesslog";
export const TAB_SCAFFOLDING = "scaffolding";
export const TAB_PERMITS = "permits";
export const TAB_NEARMISSES = "nearmisses";
export const TAB_INSPECTION = "inspection";
export const TAB_NONCONFORMANCE = "nonconformance";

const InductionAdminViewBase: React.FC<IProps> = (props) => {
    // #region Code Behind
    const { match } = useRouter();
    const { inductionid } = match.params as any;
    const [viewModel] = useState(() => InductionAdminViewModel.Instance(inductionid));
    const [selectedTab, setSelectedTab] = React.useState(StoresInstance.Domain.AccountStore.getCanEditInductionUser ? TAB_DETAILS : TAB_SITEACCESSLOG);
    const [approveOpen, setApproveOpen] = React.useState(false);

    const tabOptions: ITab[] = [
        { key: TAB_SITEACCESSLOG, title: "Site access log", hash: "siteaccesslog" },
        { key: TAB_SCAFFOLDING, title: "Scaffold", hash: "scaffolding" },
        { key: TAB_PERMITS, title: "Permits", hash: "permits" },
        { key: TAB_NEARMISSES, title: "Near misses", hash: "nearmisses" },
        { key: TAB_INSPECTION, title: "Inspection", hash: "inspection" },
        { key: TAB_NONCONFORMANCE, title: "Non-conformance", hash: "nonconformance" },
    ];

    const tabOptionsLevel5: ITab[] = [
        { key: TAB_DETAILS, title: "Details", hash: "details" },
        { key: TAB_SITEACCESSLOG, title: "Site access log", hash: "siteaccesslog" },
        { key: TAB_SCAFFOLDING, title: "Scaffold", hash: "scaffolding" },
        { key: TAB_PERMITS, title: "Permits", hash: "permits" },
        { key: TAB_NEARMISSES, title: "Near misses", hash: "nearmisses" },
        { key: TAB_INSPECTION, title: "Inspection", hash: "inspection" },
        { key: TAB_NONCONFORMANCE, title: "Non-conformance", hash: "nonconformance" },
    ];

    const handleTabClick = (tab: ITab) => {
        setSelectedTab(tab.key);
    };

    const handleApproveClose = () => {
        setApproveOpen(false);
    };

    const handleApproveInduction = () => {
        viewModel.approveInduction();
        setApproveOpen(false);
    };

    useEffect(() => {
        runInAction(async () => {
            if (!InductionAdminViewModel.GetInstance) {
                InductionAdminViewModel.Instance(inductionid);
            }
            await viewModel.loadInductionDetails();
        });
        return () => {
            if (InductionAdminViewModel.GetInstance) {
                InductionAdminViewModel.GetInstance.reset();
            }
        };
    }, []);

    useEffect(() => {
        runInAction(async () => {
            let todayDate = moment();
            todayDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
            AdminSiteAccessFilterViewModel.Instance.setStartDateFilter(todayDate.toDate());

            await viewModel.loadSiteAccess(inductionid);
        });
        return () => {
            viewModel.clean();
        };
    }, []);

    const renderForm = () => {
        if (viewModel.IsLoading) {
            return <Loader />;
        }
        switch (selectedTab) {
            case TAB_DETAILS:
                return renderDetailsForm();
            case TAB_SITEACCESSLOG:
                return renderSiteAccessLog();
            case TAB_SCAFFOLDING:
                return renderScaffolding();
            case TAB_PERMITS:
                return renderPermits();
            case TAB_NEARMISSES:
                return renderNearMisses();
            case TAB_INSPECTION:
                return renderInspection();
            case TAB_NONCONFORMANCE:
                return renderNonConformance();
            default:
                return StoresInstance.Domain.AccountStore.getCanEditInductionUser ? renderDetailsForm() : renderSiteAccessLog();
        }
    };

    const renderDetailsForm = () => {
        return <InductionAdminDetailsView viewModel={viewModel.inductionAdminItemViewModel} suppliers={viewModel.suppliers} />;
    };

    const renderSiteAccessLog = () => {
        return <InductionAdminSiteAccessLogView viewModel={viewModel} inductionUserId={inductionid} />;
    };

    const renderScaffolding = () => {
        return <InductionAdminScaffoldingView viewModel={viewModel} inductionUserId={inductionid} />;
    };

    const renderPermits = () => {
        return <InductionAdminPermitView viewModel={viewModel} inductionUserId={inductionid} />;
    };

    const renderNearMisses = () => {
        return <InductionAdminNearMissView viewModel={viewModel} inductionUserId={inductionid} />;
    };

    const renderInspection = () => {
        return <InductionAdminInspectionView viewModel={viewModel} inductionUserId={inductionid} />;
    };

    const renderNonConformance = () => {
        return <InductionAdminNonConformanceView inductionUserId={inductionid} />;
    };

    const renderPage = () => {
        return (
            <InductionPageContainer>
                <HeaderContainer>
                    <div>
                        <SHBox m={1}>
                            <PreviewImage src={viewModel.inductionAdminItemViewModel.model.photographUrl} alt="Induction photo" />
                        </SHBox>
                        <div>
                            <Typography variant="h1">{viewModel.inductionAdminItemViewModel.model.fullName}</Typography>
                            <Typography variant="h2">{viewModel.inductionAdminItemViewModel.model.priorityEmployerName}</Typography>
                        </div>
                    </div>
                    {viewModel.canApprove && (
                        <PrimaryButton displayName="Approve visitor" execute={() => setApproveOpen(true)} fullWidth={true} canExecute={!viewModel.IsLoading} />
                    )}
                </HeaderContainer>
                <Tabs
                    tabs={StoresInstance.Domain.AccountStore.getCanEditInductionUser ? tabOptionsLevel5 : tabOptions}
                    onTabClick={handleTabClick}
                    selected={selectedTab}
                    minTabWidth={132}
                    tabPadding="11.5px 8px"
                />
                {renderForm()}
                <DeleteModal
                    open={approveOpen}
                    onClose={handleApproveClose}
                    onDelete={handleApproveInduction}
                    title="Approve induction?"
                    text="Are you sure you want to approve this induction?"
                    isLoading={viewModel.IsLoading}
                />
                <SnackBar
                    messageText={viewModel.snackMessage}
                    messageType={viewModel.snackType}
                    active={viewModel.snackbarState}
                    closeOption={() => viewModel.setSnackbarState(false)}
                    autoHideDuration={5000}
                />
            </InductionPageContainer>
        );
    };

    return useObserver(() => (viewModel && viewModel.hasLoaded ? renderPage() : <Loader />));
};

export const InductionAdminView = styled(InductionAdminViewBase)``;

export const InductionPageContainer = styled(Box)`
    display: flex;
    flex: 1;
    flex-direction: column;
`;

const HeaderContainer = styled(Box)`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin: 30px 30px 15px 30px;
    justify-content: space-between;

    > div {
        display: flex;
        flex-direction: row;
    }

    > div > div:nth-child(1) {
        img {
            width: 48px;
            height: 48px;
        }
    }

    > div > div:nth-child(2) {
        display: flex;
        flex-direction: column;
    }

    button {
        max-width: 262px;
    }
`;
